<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import {
  FormRenderer,
  evaluateFormState,
  rulesNeedServerEvaluation,
  localProviderState, validateLocalValues,
} from '@sas/form-runtime'
import type {
  ProviderLoadRequest,
  RuntimeClientBundle,
  RuntimeFieldState,
} from '@sas/form-runtime'
import { api, ApiError } from '../api/client'
import type { RuntimePublication } from '../api/types'
import { useRuntimeStore } from '../stores/runtime'
import { executeClientActions, type ClientAction } from '../lib/client-actions'

const route = useRoute()
const store = useRuntimeStore()
const publicKey = computed(() => String(route.params.publicKey))
const bundle = ref<RuntimeClientBundle | null>(null)
const clientMode = ref(false)
const clientActions = ref<ClientAction[]>([])
const completedActions: Record<string, any> = {}
const publication = ref<RuntimePublication | null>(null)
const values = ref<Record<string, unknown>>({})
const states = ref<Record<string, RuntimeFieldState>>({})
const loading = ref(true)
const submitting = ref(false)
const success = ref<string | null>(null)
const error = ref<string | null>(null)
const reloadSuggested = ref(false)
function recoveryKey() { return 'sas_values:' + store.session?.workspace_id + ':' + store.session?.external_user_id + ':' + publicKey.value }
function saveRecovery() {
  if (!bundle.value) return
  const safe = Object.fromEntries(Object.entries(values.value).filter(([id]) => bundle.value!.fields[id]?.type !== 'file'))
  try { sessionStorage.setItem(recoveryKey(), JSON.stringify({ expires: Date.now() + 1800000, values: safe })) } catch { /* The current page still retains all values. */ }
}
async function reloadForm() {
  saveRecovery()
  try { await store.refreshManifest(); await load(true); reloadSuggested.value = false } catch (e) { fail(e) }
}
let generation = 0
let evaluateGeneration = 0
let alive = true
function fail(e: unknown) {
  error.value = e instanceof Error ? e.message : 'Запрос не выполнен'
  if (e instanceof ApiError && e.status === 403) { store.deny(e); if (serverEvaluateTimer) window.clearTimeout(serverEvaluateTimer); evaluateGeneration++ }
}
let serverEvaluateTimer: number | undefined

function runtimeContext(): Record<string, unknown> {
  return (store.session?.context ?? {}) as Record<string, unknown>
}

async function load(preserve = false) {
  if (!store.ready || store.blocked) { loading.value = false; return }
  const current = ++generation
  loading.value = true
  error.value = null
  try {
    const response = await store.form(publicKey.value)
    if (!alive || current !== generation || store.blocked) return
    bundle.value = response.bundle
    clientMode.value = response.runtime_mode === 'bitrix_client'
    clientActions.value = response.client_actions || []
    if (!preserve) for (const id of Object.keys(completedActions)) delete completedActions[id]
    publication.value = response.publication

    const initial: Record<string, unknown> = {}
    for (const [id, field] of Object.entries(response.bundle.fields)) {
      if (field.default !== undefined && field.default !== null) initial[id] = field.default
    }
    try {
      const recovery = JSON.parse(sessionStorage.getItem(recoveryKey()) || 'null')
      if (recovery?.expires > Date.now()) for (const [id, value] of Object.entries(recovery.values || {})) {
        if (response.bundle.fields[id] && !response.bundle.fields[id].readonly && response.bundle.fields[id].type !== 'file') initial[id] = value
      }
      sessionStorage.removeItem(recoveryKey())
    } catch { sessionStorage.removeItem(recoveryKey()) }
    values.value = initial
    await evaluateRules(initial, true)
  } catch (e) {
    if (alive && current === generation) fail(e)
  } finally {
    if (alive && current === generation) loading.value = false
  }
}

async function evaluateRules(
  sourceValues: Record<string, unknown> = values.value,
  forceServer = false,
) {
  if (!bundle.value || store.blocked) return

  // Ordinary AST rules execute locally for immediate UI response. If a future
  // rule references provider.* runtime state, keep the server endpoint as the
  // compatibility path until provider rule context is also available client-side.
  const needServer = !clientMode.value && rulesNeedServerEvaluation(bundle.value.rules)
  if (needServer && (forceServer || sourceValues === values.value)) {
    const current = ++evaluateGeneration
    const response = await store.serverCall(token => api.evaluate(token, publicKey.value, sourceValues))
    if (!alive || current !== evaluateGeneration || store.blocked) return
    states.value = response.fields
    applyImplicitValues(response.fields, sourceValues)
    return
  }

  const nextStates = evaluateFormState(bundle.value, sourceValues, runtimeContext(), localProviderState(bundle.value, sourceValues))
  states.value = nextStates
  applyImplicitValues(nextStates, sourceValues)
}

function applyImplicitValues(
  nextStates: Record<string, RuntimeFieldState>,
  sourceValues: Record<string, unknown>,
) {
  let next: Record<string, unknown> | null = null
  for (const [id, state] of Object.entries(nextStates)) {
    const hasSourceValue = Object.prototype.hasOwnProperty.call(sourceValues, id)

    // When a field already has a value, a different state.value can only come
    // from a set_value rule. Mirror the backend immediately so the UI never
    // shows a value that will be replaced on submit. For untouched fields,
    // materialize only non-null defaults.
    const shouldApplyRuleValue = hasSourceValue
      && JSON.stringify(sourceValues[id]) !== JSON.stringify(state.value)
    const shouldApplyDefault = !hasSourceValue
      && state.value !== undefined
      && state.value !== null

    if (shouldApplyRuleValue || shouldApplyDefault) {
      next ??= { ...sourceValues }
      next[id] = state.value
    }
  }
  if (next) values.value = next
}

function updateValues(next: Record<string, unknown>) {
  if (store.blocked) return
  values.value = next
  if (!bundle.value || store.blocked) return

  if (clientMode.value || !rulesNeedServerEvaluation(bundle.value.rules)) {
    void evaluateRules(next)
    return
  }

  if (serverEvaluateTimer) window.clearTimeout(serverEvaluateTimer)
  serverEvaluateTimer = window.setTimeout(() => {
    evaluateRules(values.value, true).catch(fail)
  }, 180)
}

async function providerLoader(providerId: string, request: ProviderLoadRequest = {}) {
  if (store.blocked || clientMode.value) return []
  try { return (await store.serverCall(token => api.provider(token, publicKey.value, providerId, request))).items }
  catch(e) { fail(e); throw e }
}

async function submit() {
  if (!store.ready || store.blocked || submitting.value) return
  submitting.value = true
  success.value = null
  error.value = null
  try {
    if (clientMode.value && bundle.value) {
      await evaluateRules()
      const clean = validateLocalValues(bundle.value, states.value, values.value)
      await executeClientActions(clientActions.value, clean, runtimeContext(), completedActions)
      success.value = clientActions.value.length ? 'Форма отправлена в Битрикс24.' : 'Форма заполнена. Действия отправки не настроены.'
    } else {
      const response = await store.serverCall(token => api.submit(token, publicKey.value, values.value, bundle.value?.revision))
      success.value = `Форма отправлена. Статус: ${response.submission.status}`
    }
  } catch (e) {
    saveRecovery()
    fail(e)
    if (clientMode.value || e instanceof ApiError && [409, 422].includes(e.status)) {
      try { const changed = await store.refreshManifest(); reloadSuggested.value = changed || e instanceof ApiError && e.status === 409 }
      catch { /* Keep the original error and entered values. */ }
    }
  } finally {
    submitting.value = false
  }
}

onMounted(() => load())
watch(() => store.ready, ready => { if (ready && !bundle.value) void load() })
watch(publicKey, () => { bundle.value = null; publication.value = null; values.value = {}; states.value = {}; evaluateGeneration++; if (serverEvaluateTimer) window.clearTimeout(serverEvaluateTimer); void load() })
onBeforeUnmount(() => { alive = false; generation++; evaluateGeneration++; if (serverEvaluateTimer) window.clearTimeout(serverEvaluateTimer) })
</script>

<template>
  <section>
    <nav class="breadcrumbs"><RouterLink to="/">Главная</RouterLink><span>›</span><span>{{ publication?.name || 'Форма' }}</span></nav>
    <div v-if="loading" class="empty-state">Загрузка формы…</div>
    <div v-else-if="error && !bundle" class="notice error">{{ error }}</div>
    <div v-else-if="bundle" class="form-card">
      <div class="page-head"><div><h1>{{ publication?.name }}</h1><p v-if="publication?.description">{{ publication.description }}</p></div></div>
      <div v-if="success" class="notice success">{{ success }}</div>
      <div v-if="error" class="notice error">{{ error }}</div>
      <p v-if="reloadSuggested" class="notice warning">Доступна новая версия формы. Обычные значения сохранятся; файлы при необходимости выберите повторно. <button class="button small" type="button" :disabled="submitting" @click="reloadForm">Обновить форму</button></p>
      <FormRenderer
        :bundle="bundle"
        :values="values"
        :field-states="states"
        :submitting="submitting"
        :disabled="store.blocked || Boolean(success)"
        :provider-loader="providerLoader"
        @update:values="updateValues"
        @submit="submit"
      />
    </div>
  </section>
</template>
